import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { listProducts } from "@/lib/adapters/products";
import ProductCard from "@/components/product/ProductCard";
import { ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";

export default function Refurbished() {
  const [items, setItems] = useState([]);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const scrollerRef = useRef(null);

  useEffect(() => {
    listProducts({ type: "refurbished", limit: 12 })
      .then(({ items: rows }) => setItems(rows))
      .catch(() => {});
  }, []);

  const updateEdgeState = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    setAtStart(el.scrollLeft <= 2);
    setAtEnd(el.scrollLeft >= maxScroll - 2);
  }, []);

  useEffect(() => {
    updateEdgeState();
  }, [items, updateEdgeState]);

  const scroll = (dir) => {
    const el = scrollerRef.current;
    if (el) el.scrollBy({ left: dir * el.clientWidth, behavior: "smooth" });
  };

  return (
    <section className="dyntra-container mt-10">
      <div className="flex items-end justify-between gap-4 mb-6">
        <h2 data-testid="refurb-heading" className="font-display text-2xl sm:text-3xl font-bold text-gray-900">
          Refurbished
        </h2>
        <Link
          to="/refurbished"
          data-testid="refurb-view-all"
          className="shrink-0 inline-flex items-center gap-1 text-sm font-semibold text-[#4B2AAD] hover:underline whitespace-nowrap"
        >
          View All <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
      {/* Give the rail extra horizontal padding on the outside so the last
          tile always has breathing room and the chevron button never sits
          on top of a product card. */}
      <div className="relative -mx-4 md:-mx-2 px-4 md:px-2 md:pl-12 md:pr-12">
        <button
          type="button"
          data-testid="refurb-prev"
          onClick={() => scroll(-1)}
          disabled={atStart}
          className={`hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 z-10 h-10 w-10 rounded-full bg-white border border-gray-200 shadow-md items-center justify-center transition-all ${atStart ? "opacity-30 cursor-default" : "hover:bg-[#4B2AAD] hover:text-white hover:border-[#4B2AAD]"}`}
          aria-label="Previous"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <div
          ref={scrollerRef}
          onScroll={updateEdgeState}
          className="flex gap-4 overflow-x-auto no-scrollbar snap-x snap-mandatory scroll-px-4 md:scroll-px-2 pb-2 pr-4 md:pr-8"
        >
          {items.map((p) => (
            <div
              key={p.id}
              className="snap-start shrink-0
                         min-w-[220px] max-w-[220px]
                         sm:min-w-[calc((100%-2*1rem)/3)] sm:max-w-[calc((100%-2*1rem)/3)]
                         lg:min-w-[calc((100%-5*1rem)/6)] lg:max-w-[calc((100%-5*1rem)/6)]"
            >
              <ProductCard product={{ ...p, badge: "REFURBISHED", badge_type: "refurbished" }} />
            </div>
          ))}
        </div>
        <button
          type="button"
          data-testid="refurb-next"
          onClick={() => scroll(1)}
          disabled={atEnd}
          className={`hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 z-10 h-10 w-10 rounded-full bg-white border border-gray-200 shadow-md items-center justify-center transition-all ${atEnd ? "opacity-30 cursor-default" : "hover:bg-[#4B2AAD] hover:text-white hover:border-[#4B2AAD]"}`}
          aria-label="Next"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>
    </section>
  );
}
